import React, { useEffect, useState } from "react";
import Header from "../header/header";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputBase,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SyncIcon from '@mui/icons-material/Sync';
import "./styles.css";
import PatientRecordsReport from "../patientRecordsReport/patientRecordsReport";
import FloatingButton from "../floatingButton/floatingButton";
import FloatingSyncButton from "../floatingSyncButton/floatingSyncButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import LoadingComponent from "../loadingComponent/loadingComponent";
import SelectFundusCamera from "../FundusCameraSelectionComponent/SelectFundusCamera";
import { getCurrentUserPendingPatients, getCurrentUserPendingReports, getCurrentUserId, db, getUnsyncedImageHashes, markImageHashesAsSynced, clearScreeningImageDraft } from "../../db";
import { handleGetUser } from "../../utils/patientUtils";
import { useSyncValidationContext } from "../../context/SyncValidationContext";
import { handleSetDR, getInferenceFromData } from "../../utlis";
import { isOffline as checkIsOffline } from "../../utils/offlineDetection";
import { submitReportWithPresign, multipartFallback, PresignNotSupportedError, ResumeUploadBlockedError, applyDiabetesDurationForReportApi } from "../../services/reportUploadService";
import { normalizeModelTrainingConsent } from "../../utils/modelTrainingConsent";
import { captureAppError } from "../../utils/captureAppError";

const SyncData = () => {
  const [allReports, setAllReports] = useState([]);
  const [next, setNext] = useState("");
  const [prev, setPrev] = useState("");
  const [count, setCount] = useState("");
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, count);
  const totalPages = Math.ceil(count / pageSize);
  const [getData, setGetData] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(["All Patients Screened"]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [originalReports, setOriginalReports] = useState([]);
  const [syncProgress, setSyncProgress] = useState({
    totalReports: 0,
    syncedReports: 0,
    currentPatient: '',
    currentReport: '',
    isVisible: false
  });
  const [showOfflineSnackbar, setShowOfflineSnackbar] = useState(false);
  const [showSyncSnackbar, setShowSyncSnackbar] = useState(false);
  const [syncSnackbarMessage, setSyncSnackbarMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Sync validation context
  const { refreshSyncStatus } = useSyncValidationContext();

  const options = [
    "All Patients Screened",
    "Diabetic Patients",
    "Non Diabetic Patients",
    {
      category: "Diabetic Retinopathy grade",
      grades: [
        "No Diabetic Retinopathy",
        "Mild Diabetic Retinopathy",
        "Moderate Diabetic Retinopathy",
        "Severe Diabetic Retinopathy",
        "Proliferative Diabetic Retinopathy",
      ],
    },
  ];
  const [tempSelectedOptions, setTempSelectedOptions] = useState(["All Patients Screened"]);

  useEffect(() => {
    loadPendingData();
    // Refresh sync validation status when component loads
    refreshSyncStatus();
    
    // Refresh sync validation status when component unmounts
    return () => {
      refreshSyncStatus();
    };
  }, [refreshSyncStatus]);

  // Check offline status
  useEffect(() => {
    const checkOfflineStatus = async () => {
      // Use API health check for more accurate offline detection
      const isReallyOffline = await checkIsOffline();
      setOffline(isReallyOffline);
    };

    checkOfflineStatus();
    
    // Check offline status periodically (every 5 seconds)
    const intervalId = setInterval(checkOfflineStatus, 5000);
    
    const handleOnline = async () => {
      // Verify with API health check when browser says we're online
      const isReallyOffline = await checkIsOffline();
      setOffline(isReallyOffline);
      if (!isReallyOffline) {
        refreshSyncStatus();
      }
    };
    
    // Refresh sync validation when coming back online
    const handleOnlineForSync = async () => {
      const isReallyOffline = await checkIsOffline();
      setOffline(isReallyOffline);
      if (!isReallyOffline) {
        refreshSyncStatus();
      }
    };
    
    window.addEventListener('online', handleOnlineForSync);
    const handleOffline = () => setOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('online', handleOnlineForSync);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshSyncStatus]);

  const loadPendingData = async () => {
    try {
      setLoading(true);
      const currentUserId = getCurrentUserId();
      
      // Cleanup orphaned reports for current user (reports with invalid patient_id)
      try {
        const { cleanupOrphanedReports } = await import('../../db');
        await cleanupOrphanedReports(currentUserId);
      } catch (cleanupError) {
        captureAppError(cleanupError, {
          tags: { stage: 'sync_data', errorType: 'orphan_cleanup' },
          level: 'warning',
        });
      }
      
      // Get current user's pending reports and patients
      let pendingReports = await getCurrentUserPendingReports();
      let pendingPatients = await getCurrentUserPendingPatients();
      
      // If no pending data found, try to get all data for current user as fallback
      if (pendingReports.length === 0 || pendingPatients.length === 0) {
        const allUserReports = await db.reports.where('optometrist_id').equals(currentUserId).toArray();
        const allUserPatients = await db.patients.where('optometrist_id').equals(currentUserId).toArray();
        
        // Use all data if no pending data found
        if (pendingReports.length === 0) {
          pendingReports = allUserReports;
        }
        if (pendingPatients.length === 0) {
          pendingPatients = allUserPatients;
        }
      }
      
      // Sort reports by createdAt descending (most recent first)
      pendingReports = pendingReports.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || 0);
        const dateB = new Date(b.createdAt || b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      
      
      // Refresh sync validation status after loading data
      refreshSyncStatus();
      

      // Transform data to match PatientRecordsReport format
      const transformedReports = pendingReports.map(report => {
        const patient = pendingPatients.find(p => p.id === report.patient_id);
        
        
        // Ensure patient data is complete and has required fields
        const patientData = patient ? {
          id: patient.id,
          other_id: patient.other_id || 'N/A',
          age: patient.age || 'N/A',
          sex: patient.sex || 'N/A',
          name: patient.name || 'N/A',
          ...patient
        } : {
          id: 'unknown',
          other_id: 'N/A',
          age: 'N/A',
          sex: 'N/A',
          name: 'N/A'
        };
        
        return {
          ...report,
          uuid: report.id, // Use IndexDB ID as UUID
          patient: patientData,
          ml_model: report.ml_model || { classification: {}, gradability: {}, ishazy: {} },
          feedback_data: null,
          // Add missing fields with default values
          last_visit_date: null, // Not available for pending reports
          visit_count: 1, // Each report represents one visit
          // Handle missing images
          left_image: report.left_image || null,
          right_image: report.right_image || null,
          created_at: report.created_at || report.createdAt, // Handle both field names
          updated_at: report.updated_at || report.updatedAt, // Handle both field names
          number_of_visits: 1 // Default for offline data
        };
      });

      setAllReports(transformedReports);
      setOriginalReports(transformedReports); // Make sure originalReports is set
      setCount(transformedReports.length);
      setIsSearching(false); // Reset search state when loading data
      setLoading(false);
    } catch (error) {
      captureAppError(error, { tags: { stage: 'sync_data', errorType: 'load_pending' } });
      setAllReports([]);
      setCount(0);
      setLoading(false);
    }
  };

  const handleInputChange = async (event) => {
    let newValue = event.target.value;
    setSearchValue(newValue);
    let newValuetrim = newValue.trim();

    if (newValuetrim.length >= 3 && newValuetrim !== searchValue.trim()) {
      setIsSearching(true);
      // Filter reports based on search
      const filteredReports = originalReports.filter(report => 
        report.patient.other_id?.toLowerCase().includes(newValuetrim.toLowerCase())
      );
      const filteredAndApplied = applyFilters(filteredReports, selectedOptions);
      setAllReports(filteredAndApplied);
      setCount(filteredAndApplied.length);
      setCurrentPage(1);
    } else if (newValue === "") {
      setIsSearching(false);
      const filteredReports = applyFilters(originalReports, selectedOptions);
      setAllReports(filteredReports);
      setCount(filteredReports.length);
      setCurrentPage(1);
    }
  };

  const applyFilters = (reports, filters) => {
    if (!filters || filters.includes("All Patients Screened")) {
      return reports;
    }

    return reports.filter(report => {
      // Get AI inference for this report with model_outputs support
      const leftInference = handleSetDR(
        report.ml_model?.classification?.left,
        report.ml_model?.gradability?.left,
        report.ml_model?.model_outputs?.left
      );
      const rightInference = handleSetDR(
        report.ml_model?.classification?.right,
        report.ml_model?.gradability?.right,
        report.ml_model?.model_outputs?.right
      );

      return filters.some(filter => {
        switch (filter) {
          case "Diabetic Patients":
            return report.diabetes === "Yes";
          case "Non Diabetic Patients":
            return report.diabetes === "No";
          case "No Diabetic Retinopathy":
            return leftInference === "No Diabetic Retinopathy" || rightInference === "No Diabetic Retinopathy";
          case "Mild Diabetic Retinopathy":
            return leftInference === "Mild Diabetic Retinopathy" || rightInference === "Mild Diabetic Retinopathy";
          case "Moderate Diabetic Retinopathy":
            return leftInference === "Moderate Diabetic Retinopathy" || rightInference === "Moderate Diabetic Retinopathy";
          case "Severe Diabetic Retinopathy":
            return leftInference === "Severe Diabetic Retinopathy" || rightInference === "Severe Diabetic Retinopathy";
          case "Proliferative Diabetic Retinopathy":
            return leftInference === "Proliferative Diabetic Retinopathy" || rightInference === "Proliferative Diabetic Retinopathy";
          default:
            return false;
        }
      });
    });
  };

  // Use the imported handleSetDR from utlis.js which now supports model_outputs
  // This function is kept for backward compatibility but we'll use the imported one

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allReports.slice(startIndex, endIndex);
  };

  const toggleDialog = () => {
    if (!isDialogOpen) {
      setTempSelectedOptions([...selectedOptions]);
    }
    setIsDialogOpen(!isDialogOpen);
  };

  const selectOption = (option) => {
    if (option === "All Patients Screened") {
      setTempSelectedOptions(["All Patients Screened"]);
    } else {
      setTempSelectedOptions((prev) => {
        if (prev.includes(option)) {
          return prev.filter((item) => item !== option);
        } else {
          return prev.filter((item) => item !== "All Patients Screened").concat(option);
        }
      });
    }
  };
  
  const toggleCategory = (grades) => {
    const allSelected = grades.every((grade) => tempSelectedOptions.includes(grade));
    if (allSelected) {
      setTempSelectedOptions((prev) => prev.filter((item) => !grades.includes(item)));
    } else {
      setTempSelectedOptions((prev) => {
        const withoutSpecial = prev.filter((item) => item !== "All Patients Screened");
        const newGrades = grades.filter((grade) => !prev.includes(grade));
        return [...withoutSpecial, ...newGrades];
      });
    }
  };

  const handleSyncClick = async () => {
    if (syncing) return;

    if (!navigator.onLine) {
      setShowOfflineSnackbar(true);
      return;
    }

    await runSyncAll();
  };

  const runSyncAll = async () => {
    setSyncing(true);
    
    
    try {
      const header = { "Authorization": "Token " + localStorage.getItem("optha-token") };
      
      // Get all pending patients and reports
      const pendingPatients = await db.patients.where('syncStatus').equals('pending').toArray();
      const pendingReports = await db.reports.where('syncStatus').equals('pending').toArray();
      
      // Also get synced patients that have pending reports (mixed state scenario)
      const allPatients = await db.patients.toArray();
      const allReports = await db.reports.toArray();
      
      // Find patients that have pending reports (even if patient is synced)
      const patientsWithPendingReports = allPatients.filter(patient => 
        allReports.some(report => 
          report.patient_id === patient.id && report.syncStatus === 'pending'
        )
      );
      
      // Combine pending patients with patients that have pending reports
      const patientsToSync = [...pendingPatients];
      patientsWithPendingReports.forEach(patient => {
        if (!patientsToSync.find(p => p.id === patient.id)) {
          patientsToSync.push(patient);
        }
      });
      
      
      
      // Calculate total reports for progress
      const totalReports = pendingReports.length;
      setSyncProgress({
        totalReports,
        syncedReports: 0,
        currentPatient: '',
        currentReport: '',
        isVisible: true
      });


      for (const patient of patientsToSync) {
        const patientReports = pendingReports.filter(r => r.patient_id === patient.id);
        
        try {
          setSyncProgress(prev => ({
            ...prev,
            currentPatient: `Patient ID: ${patient.other_id}`
          }));

          // Check if patient exists in backend
          const existingPatient = await handleGetUser(patient.other_id, 'other_id');
          let backendPatientId;

          if (existingPatient) {
            // Patient exists in backend, use existing patient ID
            backendPatientId = existingPatient.id;
          } else {
            // Patient does not exist, create new patient
            const body = {
              age: patient.age,
              sex: patient.sex,
              other_id: patient.other_id
            };
            const response = await axios.post(
              `${config.API_BASE_URL}/api/auth/patients/create/`,
              body,
              { headers: header }
            );

            if (response.data.id) {
              backendPatientId = response.data.id;
              
              // Update IndexDB with real patient ID (same as online flow)
              await db.patients.put({
                ...patient,
                id: response.data.id,
                syncStatus: 'synced'
              });
            } else {
              // Failed to create patient - skip to next patient
              continue;
            }
          }

          // Find all reports for this patient by other_id (same logic as online flow)
          const patientReports = pendingReports.filter(report => {
            // Get the patient data for this report from all patients (not just pending)
            const reportPatient = allPatients.find(p => p.id === report.patient_id);
            return reportPatient && reportPatient.other_id === patient.other_id;
          });
          
          let syncedReportsForPatient = 0;
          
          // Sync all reports for this patient
          for (const report of patientReports) {
            try {
              setSyncProgress(prev => ({
                ...prev,
                currentReport: `Report: ${report.id}`
              }));

              const modelTrainingConsent = normalizeModelTrainingConsent(
                report.model_training_consent
              );
              if (report.model_training_consent !== modelTrainingConsent) {
                await db.reports.update(report.id, { model_training_consent: modelTrainingConsent });
                report.model_training_consent = modelTrainingConsent;
              }

              // Call report API with backend patient ID.
              // Same orchestrator as the online submit: presigned-S3 first, multipart fallback on
              // FileSystemStorage (presign returns 400/404). Report is only deleted from IDB on
              // attach 2xx (handled by the existing reportResponse.status check below).
              const reportMetadata = {
                optometrist: report.optometrist_id,
                patient: backendPatientId,
                ml_model: report.ml_model,
                model_training_consent: modelTrainingConsent,
                diabetes: report.diabetes,
                visual_acuity_distant_left: report.visual_acuity_distant_left || '',
                visual_acuity_distant_right: report.visual_acuity_distant_right || '',
                visual_acuity_near_left: report.visual_acuity_near_left || '',
                visual_acuity_near_right: report.visual_acuity_near_right || '',
                fundus_camera: report.fundus_camera,
              };
              applyDiabetesDurationForReportApi(
                reportMetadata,
                report.diabetes,
                report.diabetes_duration_years,
                report.diabetes_duration_months,
              );
              if (report.single_image_reason) {
                reportMetadata.single_image_reason = report.single_image_reason;
              }

              const leftBlob = report.left_image
                ? new Blob([report.left_image.data], { type: report.left_image.type })
                : null;
              const rightBlob = report.right_image
                ? new Blob([report.right_image.data], { type: report.right_image.type })
                : null;
              const leftName = report.left_image?.name || null;
              const rightName = report.right_image?.name || null;

              let reportResponse;
              try {
                reportResponse = await submitReportWithPresign({
                  apiBase: config.API_BASE_URL,
                  headers: header,
                  metadata: reportMetadata,
                  leftBlob,
                  rightBlob,
                  leftName,
                  rightName,
                  existingServerUuid: report.server_report_uuid || undefined,
                  onReportUuidCreated: async (srvUuid) => {
                    await db.reports.update(report.id, { server_report_uuid: srvUuid });
                  },
                });
              } catch (presignErr) {
                if (presignErr instanceof ResumeUploadBlockedError) {
                  throw presignErr;
                }
                if (presignErr instanceof PresignNotSupportedError) {
                  reportResponse = await multipartFallback({
                    apiBase: config.API_BASE_URL,
                    headers: header,
                    metadata: reportMetadata,
                    leftBlob,
                    rightBlob,
                    leftName,
                    rightName,
                  });
                } else {
                  throw presignErr;
                }
              }

              if (reportResponse.status === 200 || reportResponse.status === 201) {
                
                // Sync image hashes for this report (same as online flow)
                try {
                  // Find image hashes for this specific report by matching the stored patient ID
                  const unsyncedHashes = await db.image_hashes
                    .where('optometrist_id').equals(report.optometrist_id)
                    .and(hash => hash.synced === false)
                    .toArray();
                  
                  // Filter hashes that belong to this report's patient
                  const reportHashes = unsyncedHashes.filter(hash => {
                    // Find the patient data for this hash
                    const hashPatient = pendingPatients.find(p => p.id === hash.patient_id);
                    const matches = hashPatient && hashPatient.other_id === patient.other_id;
                    return matches;
                  });
                  
                  if (reportHashes.length > 0) {
                    const hashesForBackend = reportHashes.map(hash => {
                      return {
                        hash: hash.hash,
                        optometrist_id: hash.optometrist_id,
                        patient_id: backendPatientId, // Use same patient ID as addreport
                        eye: hash.eye
                      };
                    });
                    
                    const hashResponse = await axios.post(
                      `${config.API_BASE_URL}/api/reports/image-hashes/sync/`,
                      { hashes: hashesForBackend },
                      { headers: header }
                    );
                    
                    if (hashResponse.status === 200 || hashResponse.status === 201) {
                      await markImageHashesAsSynced(reportHashes);
                    }
                  }
                } catch (error) {
                  captureAppError(error, {
                    tags: { stage: 'sync', errorType: 'report_image_hashes' },
                    extra: { reportId: report.id, patientOtherId: patient.other_id },
                    level: 'warning',
                  });
                }
                
                // Delete report from IndexDB on success
                try {
                  await db.reports.delete(report.id);
                } catch (deleteError) {
                  console.error('Error deleting report from IndexDB:', deleteError);
                  captureAppError(deleteError, {
                    tags: { stage: 'sync', errorType: 'delete_report_idb' },
                    extra: { reportId: report.id },
                    level: 'warning',
                  });
                }
                
                syncedReportsForPatient++;
                setSyncProgress(prev => ({
                  ...prev,
                  syncedReports: prev.syncedReports + 1
                }));
              } else {
                captureAppError(
                  new Error(`Sync report unexpected status: ${reportResponse?.status}`),
                  {
                    tags: { stage: 'sync', errorType: 'report_unexpected_status' },
                    extra: {
                      reportId: report.id,
                      patientOtherId: patient.other_id,
                      status: reportResponse?.status,
                    },
                  }
                );
              }
            } catch (error) {
              if (!(error instanceof ResumeUploadBlockedError)) {
                captureAppError(error, {
                  tags: { stage: 'sync', errorType: 'report_upload' },
                  extra: { reportId: report.id, patientOtherId: patient.other_id },
                });
              } else {
                captureAppError(error, {
                  tags: { stage: 'sync', errorType: 'resume_upload_blocked' },
                  level: 'warning',
                });
              }
            }
          }
          
          // Only delete patient if ALL reports for this patient were synced successfully
          if (syncedReportsForPatient === patientReports.length) {
            try {
              // Use cascade deletion to ensure all reports are deleted with patient
              const { deletePatientWithCascade } = await import('../../db');
              const deleteResult = await deletePatientWithCascade(patient.id);
              
              if (deleteResult.success) {
                console.log(`✅ Deleted patient and ${deleteResult.deletedReports} associated report(s) after sync`);
              } else {
                console.error('Error deleting patient with cascade:', deleteResult.error);
              }
            } catch (deleteError) {
              console.error('Error deleting patient from IndexDB:', deleteError);
              captureAppError(deleteError, {
                tags: { stage: 'sync', errorType: 'delete_patient_idb' },
                extra: { patientId: patient.id, patientOtherId: patient.other_id },
                level: 'warning',
              });
            }
          } else {
            // Only partial reports synced for patient - keeping patient in IndexDB
          }
        } catch (error) {
          captureAppError(error, {
            tags: { stage: 'sync', errorType: 'patient_sync' },
            extra: { patientId: patient.id, patientOtherId: patient.other_id },
          });
        }
      }
      
      // Hide progress bar and show completion message
      setSyncProgress(prev => ({
        ...prev,
        isVisible: false
      }));
      
      setSyncSnackbarMessage('Sync completed!');
      setShowSyncSnackbar(true);
      await clearScreeningImageDraft();
      loadPendingData(); // Reload data after sync
      
      // Update dashboard stats to reflect new sync time
      try {
        const userId = getCurrentUserId();
        if (userId) {
          const currentTime = new Date().toISOString();
          await db.dashboard_stats.put({
            id: 'dashboard_stats',
            data: {}, // Empty data, just updating timestamp
            lastUpdated: currentTime,
            optometrist_id: userId
          });
        }
      } catch (error) {
        captureAppError(error, {
          tags: { stage: 'sync', errorType: 'dashboard_stats_update' },
          level: 'warning',
        });
      }
      
      // Image hashes are now synced after each report (above)
      // Sync image hashes to backend
      try {
        const unsyncedHashes = await getUnsyncedImageHashes();
        
        if (unsyncedHashes.length > 0) {
          
          // Create mapping of stored patient IDs to backend patient IDs (same logic as addreport)
          const patientIdMapping = new Map();
          for (const patient of pendingPatients) {
            const existingPatient = await handleGetUser(patient.other_id, 'other_id');
            if (existingPatient) {
              patientIdMapping.set(patient.id, existingPatient.id);
            }
          }
          
          // Format hashes for backend (use mapped patient ID)
          const hashesForBackend = unsyncedHashes.map(hash => {
            const backendPatientId = patientIdMapping.get(hash.patient_id) || hash.patient_id;
            return {
              hash: hash.hash,
              optometrist_id: hash.optometrist_id, // Use same format as stored
              patient_id: backendPatientId, // Use mapped patient ID (same as addreport)
              eye: hash.eye
            };
          });
          
          const response = await axios.post(
            `${config.API_BASE_URL}/api/reports/image-hashes/sync/`,
            { hashes: hashesForBackend },
            { headers: header }
          );
          
          if (response.status === 200 || response.status === 201) {
            // Mark hashes as synced
            await markImageHashesAsSynced(unsyncedHashes);
          } else {
            // Failed to sync image hashes
          }
        } else {
        }
      } catch (error) {
        captureAppError(error, {
          tags: { stage: 'sync', errorType: 'bulk_image_hashes' },
          level: 'warning',
        });
      }

      // Refresh sync validation status to clear warnings/blocks
      refreshSyncStatus();
    } catch (error) {
      captureAppError(error, { tags: { stage: 'sync', errorType: 'sync_all' } });
      setSyncSnackbarMessage('Sync failed. Please try again.');
      setShowSyncSnackbar(true);
      
      // Hide progress bar on error
      setSyncProgress(prev => ({
        ...prev,
        isVisible: false
      }));
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {!isMobile && <Header />}
      
      {/* Sync progress: full-screen loading with "Your data is being synced" + progress bar */}
      {syncProgress.isVisible && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(249, 252, 255, 1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: { xs: '90%', md: '400px' },
              width: '100%',
              px: 3,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <img
                src="/assests/images/InferenceImage.svg"
                alt=""
                style={{
                  width: 64,
                  height: 64,
                  animation: 'spin 2s linear infinite',
                }}
              />
            </Box>
            <Typography
              fontWeight={600}
              fontSize={{ xs: '16px', md: '20px' }}
              sx={{ color: '#0D1E83', mb: 2, textAlign: 'center' }}
            >
              Your data is being synced
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={
                  syncProgress.totalReports > 0
                    ? (syncProgress.syncedReports / syncProgress.totalReports) * 100
                    : 0
                }
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(13, 30, 131, 0.12)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#0D1E83',
                  },
                }}
              />
            </Box>
          </Box>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </Box>
      )}
      
      {/* Offline state: persistent banner is shown globally in App.js – no duplicate here */}

      {/* Sync Result Snackbar */}
      <Snackbar
        open={showSyncSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSyncSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSyncSnackbar(false)} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {syncSnackbarMessage}
        </Alert>
      </Snackbar>

      <div className="recordsDiv">
        <div className="searchDiv">
          <Paper className="searchBar" sx={isMobile ? {width:"100%"} : {}}>
            <IconButton>
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, '& input::placeholder': {fontStyle: 'italic'}, }}
              placeholder="Search by Patient ID"
              inputProps={{ "aria-label": "search patient reports" }}
              onChange={handleInputChange}
              value={searchValue}
            />
          </Paper>
        </div>
        <div className="filterDiv">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "4px",
              background: "#EDEDED",
              cursor: "pointer",
            }}
            onClick={() => toggleDialog()}
          >
            <div
              style={{
                background: "#011F4B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
              }}
            >
              <FilterAltOutlinedIcon style={{ color: "#FFFFFF" }} />
            </div>
            {!isMobile && <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                padding: "4px",
              }}
            >
              {selectedOptions.join(", ")}
            </div>}
            <div>
              <Dialog open={isDialogOpen}>
                <DialogTitle
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "#051572",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <FilterAltOutlinedIcon sx={{ color: "#051572", mr: 1 }} />
                    <Typography fontWeight="bold">Filter Patients By</Typography>
                  </Box>
                  <IconButton onClick={toggleDialog}>
                    <CloseOutlinedIcon sx={{ color: "#051572" }} />
                  </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 2, pt: 0 }}>
                  {options.map((option) =>
                    typeof option === "string" ? (
                      <Box key={option}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={tempSelectedOptions.includes(option)}
                              onChange={() => selectOption(option)}
                              sx={{
                                "&.Mui-checked": {
                                        color: "#0D1E83", 
                                      },
                              }}
                            />
                          }
                          label={<Typography sx={{ color: "#051572" }}>{option}</Typography>}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ ml: 0 }} // ensures one checkbox per row
                        />
                        <Divider sx={{ my: 1 }} />
                      </Box>
                    ) : (
                      <Box key={option.category}>
                      {/* Parent category with checkbox */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={option.grades.every((grade) => tempSelectedOptions.includes(grade))}
                            indeterminate={
                              option.grades.some((grade) => tempSelectedOptions.includes(grade)) &&
                              !option.grades.every((grade) => tempSelectedOptions.includes(grade))
                            }
                            onChange={() => toggleCategory(option.grades)}
                            sx={{
                              "&.Mui-checked": {
                                      color: "#0D1E83", 
                                    },
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: "#051572",
                              "&.Mui-checked": {
                                      color: "#0D1E83", 
                                    },
                            }}
                          >
                            {option.category}
                          </Typography>
                        }
                        sx={{ pl: 1.8, height: 40 }}
                      />
                    
                      {/* Child checkboxes */}
                      {option.grades.map((grade) => (
                        <div key={grade}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={tempSelectedOptions.includes(grade)}
                              onChange={() => selectOption(grade)}
                              sx={{
                                "&.Mui-checked": {
                                        color: "#0D1E83", 
                                      },
                              }}
                            />
                          }
                          label={<Typography sx={{ color: "#051572" }}>{grade}</Typography>}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ ml: 3 }}
                        />
                        </div>
                      ))}
                    
                      <Divider sx={{ my: 1 }} />
                    </Box>
                    

                    )
                  )}
                </DialogContent>

                {/* Footer */}
                <Box
                  sx={{
                    display: "flex",
                    background: "#F0F1F4",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    p: 2,
                  }}
                >
                  <Typography
                    onClick={toggleDialog}
                    sx={{ cursor: "pointer", fontWeight: 600, color: "#051572", textTransform: "none" }}
                  >
                    Close
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography
                    onClick={() => {
                      setSelectedOptions([...tempSelectedOptions]);
                      const filteredReports = applyFilters(originalReports, tempSelectedOptions);
                      setAllReports(filteredReports);
                      setCount(filteredReports.length);
                      setCurrentPage(1);
                      toggleDialog();
                    }}
                    sx={{ cursor: "pointer", fontWeight: 600, color: "#051572" }}
                  >
                    Apply
                  </Typography>
                </Box>
              </Dialog>


            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Typography color="#949494" fontWeight="300" fontSize={isMobile?"12px":"16px"}>
              {count > 0
                ? `${startRecord}-${endRecord} out of ${count}`
                : `0-${endRecord} out of ${count}`}
            </Typography>
            
            {/* Pagination Arrows */}
            {totalPages > 1 && (
              <>
                <IconButton
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  size="small"
                  sx={{
                    color: currentPage === 1 ? '#ccc' : '#0D1E83',
                    '&:hover': {
                      backgroundColor: currentPage === 1 ? 'transparent' : 'rgba(13, 30, 131, 0.04)'
                    },
                    '&:disabled': {
                      color: '#ccc'
                    }
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                
                <IconButton
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  size="small"
                  sx={{
                    color: currentPage === totalPages ? '#ccc' : '#0D1E83',
                    '&:hover': {
                      backgroundColor: currentPage === totalPages ? 'transparent' : 'rgba(13, 30, 131, 0.04)'
                    },
                    '&:disabled': {
                      color: '#ccc'
                    }
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}
          </div>
        </div>
        {count > 0 ? (
          <>
            <div className="patientRecordsGrid">
              {getCurrentPageItems().map((report, index) => {
                // Add safety checks for the report structure
                if (!report || !report.ml_model) {
                  return (
                    <PatientRecordsReport
                      key={index}
                      report={report}
                      inference={{ left: undefined, right: undefined }}
                      isMobile={isMobile}
                      dataSource="indexdb"
                    />
                  );
                }

                // Same inference as detail page so list referral matches /patientrecord/:id
                const leftInference = report.feedback_data?.left_eye_correct_inference ||
                  getInferenceFromData(report.ml_model?.classification?.left, report.ml_model?.model_outputs?.left);
                const rightInference = report.feedback_data?.right_eye_correct_inference ||
                  getInferenceFromData(report.ml_model?.classification?.right, report.ml_model?.model_outputs?.right);
                const finalInference = { left: leftInference, right: rightInference };

                // Same feedback-corrected-first preference as DR above, applied to DME
                const leftDME = report.feedback_data?.left_eye_correct_dme ||
                  report.ml_model?.model_outputs?.left?.drdme?.DME_grade || null;
                const rightDME = report.feedback_data?.right_eye_correct_dme ||
                  report.ml_model?.model_outputs?.right?.drdme?.DME_grade || null;
                const finalDME = { left: leftDME, right: rightDME };

                return (
                  <PatientRecordsReport
                    key={index}
                    report={report}
                    inference={finalInference}
                    dme={finalDME}
                    isMobile={isMobile}
                    dataSource="indexdb"
                  />
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '50vh',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {isSearching && searchValue.trim().length >= 3 ? (
              <>
                <Typography variant="h5" color="textSecondary" sx={{ fontWeight: 500 }}>
                  No Patient Found
                </Typography>
              </>
            ) : (
              <>
                {/* Sync Icon */}
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <SyncIcon sx={{ fontSize: "40px", color: "#4caf50" }} />
                </Box>

                {/* No Data to Sync Message */}
                <Typography variant="h5" color="textSecondary" sx={{ fontWeight: 500 }}>
                  No data to sync
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center' }}>
                  All pending data has been successfully synced to the server.
                </Typography>
              </>
            )}
          </div>
        )}
      </div>
      <LoadingComponent open={getData} text={false} />
      
      {/* Offline Snackbar */}
      <Snackbar
        open={showOfflineSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowOfflineSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowOfflineSnackbar(false)} 
          severity="warning" 
          sx={{ width: '100%' }}
        >
          Please check your internet connection and try again.
        </Alert>
      </Snackbar>
      
      {/* Floating Sync Button */}
      <FloatingSyncButton
        onClick={handleSyncClick}
        disabled={syncing || count === 0 || offline}
        syncing={syncing}
        count={count}
      />
    </>
  );
};

export default SyncData; 